import {
  Button,
  Card,
  Icon,
  Modal,
  Progress,
  Result,
  Spin,
  Typography,
  message,
} from "antd";
import CenteredLayout from "components/CenteredLayout";
import strings from "i18n/strings";
import React from "react";
import VirtualList from "react-tiny-virtual-list";
import matterId from "services/matterId";
import upload from "services/upload";
import api from "../../services/api";
import UploadInfo from "./UploadInfo";
const { Paragraph } = Typography;

class Upload extends React.Component {
  constructor(props) {
    super(props);
    this.selectionVersion = 0;
    this.livePreviewUrls = new Set();
    this.state = {
      isUploading: false,
      error: null,
      loading: strings.loadingAlbum,
      uploadedFiles: [],
      albumStatus: null,
      exceedsLimit: false,
      confirmationOpen: false,
      previewUrls: [],
    };
  }

  handleUploadClick() {
    this.refs.input.click();
  }

  createPreviewUrls(files) {
    return files.slice(0, 8).map((file) => {
      try {
        const url = URL.createObjectURL(file);
        this.livePreviewUrls.add(url);
        return url;
      } catch (error) {
        return null;
      }
    });
  }

  revokePreviewUrls(urls = this.livePreviewUrls) {
    urls.forEach((url) => {
      if (this.livePreviewUrls.delete(url)) URL.revokeObjectURL(url);
    });
  }

  componentWillUnmount() {
    // Pending album checks must not allocate previews or update state later.
    this.selectionVersion += 1;
    this.revokePreviewUrls();
  }

  openUploadConfirmation() {
    const files = this.state.filesToUpload;
    if (this.uploadStarted || this.state.exceedsLimit || !files || !files.length)
      return;
    this.setState({
      confirmationOpen: true,
      previewUrls: this.state.previewUrls.length
        ? this.state.previewUrls
        : this.createPreviewUrls(files),
    });
  }

  closeUploadConfirmation() {
    if (this.uploadStarted) return;
    this.revokePreviewUrls();
    this.setState({ confirmationOpen: false, previewUrls: [] });
  }

  handlePreviewError(url) {
    if (!this.livePreviewUrls.has(url)) return;
    this.revokePreviewUrls([url]);
    this.setState((state) => ({
      previewUrls: state.previewUrls.map((preview) =>
        preview === url ? null : preview
      ),
    }));
  }

  async confirmUpload() {
    const files = this.state.filesToUpload;
    if (
      this.uploadStarted ||
      !this.state.confirmationOpen ||
      this.state.exceedsLimit ||
      !files ||
      !files.length
    ) return;
    this.uploadStarted = true;
    this.revokePreviewUrls();
    this.setState({ previewUrls: [], confirmationOpen: false });
    await this.upload();
  }

  async upload() {
    this.setState({ isUploading: true });

    await upload(
      this.props.user.id,
      matterId,
      this.state.filesToUpload,
      (progress, uploadedFiles) => {
        this.setState({ uploadProgress: progress - 1, uploadedFiles });
      },
      (error) => {
        this.setState({ error });
      }
    );

    this.setState({ finishedUpload: true, uploadProgress: 100 });
  }

  async setFileList() {
    if (this.uploadStarted) return;
    const selectionVersion = ++this.selectionVersion;
    const selectedFiles = Array.from(this.refs.input.files || []);
    // A new selection supersedes the previous pending selection, even if it
    // contains no valid JPEGs.
    this.revokePreviewUrls();
    this.setState({
      filesToUpload: undefined,
      totalSize: 0,
      exceedsLimit: false,
      previewUrls: [],
      confirmationOpen: false,
      loading: strings.scanningFiles,
    });
    const filesToUpload = [];
    let tooLargeJPEGsCount = 0;
    let totalSize = 0;
    /* eslint-disable no-unused-vars */
    for (let file of selectedFiles) {
      if (file.type === "image/jpeg") {
        if (file.size < 1024 * 1024 * 5) {
          totalSize += file.size;
          filesToUpload.push(file);
        } else {
          tooLargeJPEGsCount += 1;
        }
      }
    }
    /* eslint-enable no-unused-vars */
    if (filesToUpload.length === 0) {
      this.setState({ loading: false });
      return tooLargeJPEGsCount
        ? message.error(strings.filesAreTooBig)
        : message.error(strings.noJPEGsFound);
    }

    if (tooLargeJPEGsCount)
      message.warning(strings.filesAreTooBigWithCount(tooLargeJPEGsCount));

    // Check album status and limits
    try {
      const albumStatus = await api.getAlbumItemsStatus(matterId);
      if (selectionVersion !== this.selectionVersion) return;
      const exceedsLimit =
        albumStatus.hasLimit &&
        filesToUpload.length > albumStatus.remainingItems;

      this.setState({
        filesToUpload,
        totalSize,
        albumStatus,
        exceedsLimit,
        loading: false,
        previewUrls: this.createPreviewUrls(filesToUpload),
      });
    } catch (error) {
      if (selectionVersion !== this.selectionVersion) return;
      this.setState({ error, loading: false });
    }
  }

  renderSpinner() {
    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Spin />{" "}
        <span style={{ display: "inline-block", width: "12px" }}> </span>
        {this.state.loading}
      </div>
    );
  }

  renderPreuploadStatistics() {
    return (
      <React.Fragment>
        <Card size="small">
          <VirtualList
            width="100%"
            height={200}
            itemCount={this.state.filesToUpload.length}
            itemSize={24}
            renderItem={({ index, style }) => (
              <pre key={index} style={style}>
                {~this.state.uploadedFiles.indexOf(
                  this.state.filesToUpload[index]
                ) ? (
                  <Icon
                    type="check-circle"
                    theme="twoTone"
                    twoToneColor="#52c41a"
                  />
                ) : (
                  <Icon type="file-image" />
                )}{" "}
                {index + 1}.{" "}
                {this.state.filesToUpload[index].webkitRelativePath} [
                {(
                  this.state.filesToUpload[index].size /
                  1024 /
                  1024
                ).toPrecision(2)}
                MB]
              </pre>
            )}
          />
        </Card>
        <br />
        {!this.state.isUploading ? (
          <React.Fragment>
            <Paragraph type={this.state.exceedsLimit ? "danger" : undefined}>
              {this.state.exceedsLimit && this.state.albumStatus
                ? strings.uploadLimitExceeded(
                    this.state.filesToUpload.length,
                    this.state.albumStatus.remainingItems,
                    this.state.albumStatus.currentItemsCount,
                    this.state.albumStatus.itemsLimit
                  )
                : strings.uploadSummary(
                    this.state.filesToUpload.length,
                    this.state.totalSize
                  )}
            </Paragraph>
            <Button
              type="primary"
              onClick={() => this.openUploadConfirmation()}
              disabled={this.state.exceedsLimit}
            >
              <Icon type="upload" /> {strings.startUpload}
            </Button>{" "}
            <Button onClick={() => this.handleUploadClick()}>
              <Icon type="folder-open" /> {strings.changeDir}
            </Button>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Typography>
              {this.state.finishedUpload ? (
                <React.Fragment>
                  {strings.uploadFinished}{" "}
                  <a href={window.location.href}>{strings.makeAnotherUpload}</a>
                </React.Fragment>
              ) : (
                strings.uploading
              )}
            </Typography>
            <Progress percent={this.state.uploadProgress} />
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }

  renderDirectoryChoser() {
    return (
      <React.Fragment>
        <Paragraph>{strings.chooseDirWithPhotos}</Paragraph>
        <Paragraph>
          <Icon theme="twoTone" twoToneColor="#eb2f96" type="warning" />{" "}
          {strings.imageRequirements}
        </Paragraph>
        <Button type="primary" onClick={() => this.handleUploadClick()}>
          <Icon type="folder-open" />
          {strings.chooseDir}
        </Button>
        <Paragraph type="secondary">
          <br />
          {strings.dirRequirements}
        </Paragraph>
      </React.Fragment>
    );
  }

  render() {
    const filesToUpload = this.state.filesToUpload || [];
    if (this.state.error)
      return (
        <CenteredLayout>
          <Result
            status="error"
            title={`${this.state.error.message}`}
            subTitle={JSON.stringify(this.state.error)}
          />
        </CenteredLayout>
      );

    return (
      <CenteredLayout>
        <div style={{ width: "900px" }}>
          <UploadInfo
            onLoadingError={(error) => this.setState({ error })}
            onSuccess={() => this.setState({ loading: false })}
            onLogout={async () => {
              await api.endSession();
              window.location.href = "" + window.location.href;
            }}
            user={this.props.user}
          />
          <br />
          {this.state.loading ? (
            this.renderSpinner()
          ) : (
            <Card>
              {this.state.filesToUpload
                ? this.renderPreuploadStatistics()
                : this.renderDirectoryChoser()}
            </Card>
          )}
          <input
            hidden={true}
            name="dir"
            id="dir_input"
            type="file"
            ref="input"
            webkitdirectory="true"
            directory="true"
            multiple="multiple"
            onChange={() => this.setFileList()}
          />
          <Modal
            title={strings.confirmUploadTitle}
            visible={this.state.confirmationOpen}
            onOk={() => this.confirmUpload()}
            onCancel={() => this.closeUploadConfirmation()}
            okText={strings.confirmUpload}
            cancelText={strings.cancelUpload}
            okButtonProps={{
              disabled: this.state.exceedsLimit || !filesToUpload.length,
            }}
            maskClosable={true}
            keyboard={true}
            destroyOnClose={true}
          >
            <p>{strings.confirmUploadCount(filesToUpload.length)}</p>
            <div className="upload-preview-grid">
              {this.state.previewUrls.map((url, index) => (
                <div
                  className="upload-preview"
                  key={`${index}-${url || "unavailable"}`}
                >
                  {url ? (
                    <img
                      src={url}
                      alt={strings.photoPreview(index + 1)}
                      onError={() => this.handlePreviewError(url)}
                    />
                  ) : (
                    <span>{strings.previewUnavailable}</span>
                  )}
                  <span className="upload-preview-number">{index + 1}</span>
                </div>
              ))}
            </div>
            {filesToUpload.length > 8 && (
              <p>{strings.morePhotos(filesToUpload.length - 8)}</p>
            )}
          </Modal>
        </div>
      </CenteredLayout>
    );
  }
}

export default Upload;
